import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { Autocomplete } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { TeamLearningFilter } from '../molecules/TeamLearningFilter'
import { AppTextField } from 'src/feature/form/component/analog/AppTextField'
import { ComparisonEntity, EntityMetrics } from '../../model/my-team-comparison'
import { PageMetrics } from '../molecules/PageMetrics'
import { SkillsTable } from '../molecules/SkillsTable'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { useMyTeamStore } from '../../store/my-team.store'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { RatingFilter } from '../molecules/RatingFilter'
import { useRatingFilter } from '../../hook/useRatingFilter'
import { SkillGrowthChart } from '../molecules/SkillGrowthChart'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'

interface TeamComparisonViewProps {
  isSidebarCollapsed: boolean
}

export const TeamComparisonView = observer(({ isSidebarCollapsed }: TeamComparisonViewProps) => {
  const myTeamStore = useMyTeamStore()
  const { translation } = useLocalizationStore()
  const { isLoading: isLoadingOptions } = myTeamStore.getGenericFieldsState('getComparisonTeams')
  const { isLoading: isLoadingComparison } = myTeamStore.getGenericFieldsState('compareTeams')
  
  const [entity1, setEntity1] = useState<ComparisonEntity | null>(null)
  const [entity1FromDate, setEntity1FromDate] = useState<Dayjs | null>(dayjs().subtract(1, 'month'))
  const [entity1ToDate, setEntity1ToDate] = useState<Dayjs | null>(dayjs())
  
  const [entity2, setEntity2] = useState<ComparisonEntity | null>(null)
  const [entity2FromDate, setEntity2FromDate] = useState<Dayjs | null>(dayjs().subtract(1, 'month'))
  const [entity2ToDate, setEntity2ToDate] = useState<Dayjs | null>(dayjs())
  
  const {
    minRating,
    maxRating,
    handleRatingClick,
    isRatingSelected,
    resetRating,
  } = useRatingFilter()

  // Used to hide som metrics
  const [isMetricsExpanded, setIsMetricsExpanded] = useState(false)

  // Fetch teams on mount and reset comparison data
  useEffect(() => {
    myTeamStore.getComparisonTeams()
    myTeamStore.teamComparison = undefined
  }, [myTeamStore])

  const comparisonData = myTeamStore.teamComparison
  const isDualMode = Boolean(comparisonData?.entity2)

  const formatMetricValue = (value: any, isPercentage = false, decimals = 2): string | number => {
    if (value === null || value === undefined || value === '') {
      return '-'
    }

    if (typeof value === 'number') {
      const formatted = value.toFixed(decimals)

      return isPercentage ? `${formatted}%` : formatted
    }

    return value
  }

  const buildGeneralMetrics = (entityData: EntityMetrics | undefined) => {
    if (!entityData) {
      return []
    }

    return [
      { label: translation.pages.myTeam.metricLabels.avgSkillLevel(), value: formatMetricValue(entityData.metrics.averageSkillLevel), tootlTipDesc: translation.pages.myTeam.metricTooltips.averageSkillLevel() },
      { label: translation.pages.myTeam.metricLabels.aboveExpected(), value: formatMetricValue(entityData.metrics.skillsAboveExpected), tootlTipDesc: translation.pages.myTeam.metricTooltips.skillsAboveExpected() },
      { label: translation.pages.myTeam.metricLabels.belowExpected(), value: formatMetricValue(entityData.metrics.skillsBelowExpected), tootlTipDesc: translation.pages.myTeam.metricTooltips.skillsBelowExpected() },
      { label: translation.pages.myTeam.metricLabels.userCount(), value: formatMetricValue(entityData.metrics.userCount), tootlTipDesc: translation.pages.myTeam.metricTooltips.userCount() },
      { label: translation.pages.myTeam.metricLabels.totalAssessed(), value: formatMetricValue(entityData.metrics.totalSkillsAssessed), tootlTipDesc: translation.pages.myTeam.metricTooltips.totalSkillsAssessed() },
      { label: translation.pages.myTeam.metricLabels.validated(), value: formatMetricValue(entityData.metrics.validatedCount), tootlTipDesc: translation.pages.myTeam.metricTooltips.validatedCount() },
      { label: translation.pages.myTeam.metricLabels.coverage(), value: formatMetricValue(entityData.metrics.skillCoverage, true), valueColor: true, tootlTipDesc: translation.pages.myTeam.metricTooltips.skillCoverage() },
      { label: translation.pages.myTeam.metricLabels.progressRate(), value: formatMetricValue(entityData.metrics.progressRate), tootlTipDesc: translation.pages.myTeam.metricTooltips.progressRate() },
    ]
  }

  const buildTeamSpecificMetrics = (specificMetrics: any) => {
    if (!specificMetrics) {
      return []
    }

    return [
      { label: translation.pages.myTeam.metricLabels.teamHealth(), value: formatMetricValue(specificMetrics.teamHealth, true), valueColor: true, tootlTipDesc: translation.pages.myTeam.metricTooltips.teamHealth() },
      { label: translation.pages.myTeam.metricLabels.collaborationScore(), value: formatMetricValue(specificMetrics.collaborationScore, true), valueColor: true, tootlTipDesc: translation.pages.myTeam.metricTooltips.collaborationScore() },
      { label: translation.pages.myTeam.metricLabels.capacityUtilization(), value: formatMetricValue(specificMetrics.capacityUtilization, true), valueColor: true, tootlTipDesc: translation.pages.myTeam.metricTooltips.capacityUtilization() },
      { label: translation.pages.myTeam.metricLabels.skillDiversity(), value: formatMetricValue(specificMetrics.skillDiversity, true), valueColor: true, tootlTipDesc: translation.pages.myTeam.metricTooltips.skillDiversity() },
    ]
  }

  const generalMetrics = useMemo(
    () => buildGeneralMetrics(comparisonData?.entity1),
    [comparisonData, translation],
  )

  const teamSpecificMetrics = useMemo(
    () => buildTeamSpecificMetrics(comparisonData?.entity1?.specificMetrics),
    [comparisonData, translation],
  )

  const entity2GeneralMetrics = useMemo(
    () => buildGeneralMetrics(comparisonData?.entity2),
    [comparisonData, translation],
  )

  const entity2TeamSpecificMetrics = useMemo(
    () => buildTeamSpecificMetrics(comparisonData?.entity2?.specificMetrics),
    [comparisonData, translation],
  )

  // Filter metrics to show only avgSkillLevel, aboveExpected, belowExpected and userCount first
  const getFilteredMetrics = (metrics: Array<any>) => {
    if (isMetricsExpanded) {
      return metrics
    }
    
    // Labels to show
    const labelsToShow = [
      translation.pages.myTeam.metricLabels.avgSkillLevel(),
      translation.pages.myTeam.metricLabels.aboveExpected(),
      translation.pages.myTeam.metricLabels.belowExpected(),
      translation.pages.myTeam.metricLabels.userCount(),
    ]

    return metrics.filter(metric => labelsToShow.includes(metric.label))
  }

  const commonComparisonMetrics = useMemo(() => {
    if (!comparisonData?.commonMetrics) {
      return []
    }

    return [
      { label: translation.pages.myTeam.metricLabels.overallAlignment(), value: formatMetricValue(comparisonData.commonMetrics.overallAlignment, true), valueColor: true, tootlTipDesc: translation.pages.myTeam.metricTooltips.overallAlignment() },
      { label: translation.pages.myTeam.metricLabels.sharedStrengths(), value: formatMetricValue(comparisonData.commonMetrics.sharedStrengths), tootlTipDesc: translation.pages.myTeam.metricTooltips.sharedStrengths() },
      { label: translation.pages.myTeam.metricLabels.sharedGaps(), value: formatMetricValue(comparisonData.commonMetrics.sharedGaps), tootlTipDesc: translation.pages.myTeam.metricTooltips.sharedGaps() },
      { label: translation.pages.myTeam.metricLabels.complementarity(), value: formatMetricValue(comparisonData.commonMetrics.complementarity), tootlTipDesc: translation.pages.myTeam.metricTooltips.complementarity() },
    ]
  }, [comparisonData, translation])

  const handleCompare = () => {
    if (!entity1 || !entity1FromDate || !entity1ToDate) {
      return
    }

    myTeamStore.compareTeams(
      entity1.id,
      entity1FromDate.format('YYYY-MM-DD'),
      entity1ToDate.format('YYYY-MM-DD'),
      entity2?.id,
      entity2FromDate?.format('YYYY-MM-DD'),
      entity2ToDate?.format('YYYY-MM-DD'),
      minRating ?? undefined,
      maxRating ?? undefined,
    )
  }

  const handleReset = () => {
    setEntity1(null)
    setEntity1FromDate(dayjs().subtract(1, 'month'))
    setEntity1ToDate(dayjs())
    setEntity2(null)
    setEntity2FromDate(dayjs().subtract(1, 'month'))
    setEntity2ToDate(dayjs())
    resetRating()
    myTeamStore.teamComparison = undefined
  }

  const renderEntityContent = (
    entityData: EntityMetrics,
    generalMetricsData: Array<{ label: string; value: string | number; valueColor?: boolean; tootlTipDesc?: string }>,
    specificMetricsData: Array<{ label: string; value: string | number; valueColor?: boolean; tootlTipDesc?: string }>,
  ) => (
    <AppBox sx={styles.entityColumn}>
      <AppBox flexDirection='column' gap={5}>
        <Caption sx={styles.entityName}>{entityData.name}</Caption>
        <Caption sx={styles.period}>{entityData.period}</Caption>
      </AppBox>
      
      <AppBox flexDirection='column' gap={5}>
        <Caption sx={styles.metricsTitle}>{translation.pages.myTeam.metricLabels.generalMetrics()}</Caption>
        <PageMetrics
          sx={styles.metricsGrid}
          metrics={getFilteredMetrics(generalMetricsData)}
          isLoading={isLoadingComparison}
          size="small"
        />

        {isMetricsExpanded && specificMetricsData.length > 0 && (
          <AppBox flexDirection='column' gap={5}>
            <Caption sx={styles.metricsTitle}>{translation.pages.myTeam.metricLabels.teamSpecificMetrics()}</Caption>
            <PageMetrics
              sx={styles.metricsGrid}
              metrics={specificMetricsData}
              isLoading={isLoadingComparison}
              size="small"
            />
          </AppBox>
        )}
        <AppBox
          sx={styles.expandButton}
          onClick={() => setIsMetricsExpanded(prev => !prev)}
        >
          <Caption sx={styles.expandText}>
            {isMetricsExpanded
              ? translation.pages.myTeam.metricsExpand.showLess()
              : translation.pages.myTeam.metricsExpand.showMore()}
          </Caption>

          {isMetricsExpanded
            ? <KeyboardArrowUpIcon fontSize="small" />
            : <KeyboardArrowDownIcon fontSize="small" />
          }
        </AppBox>
      </AppBox>

      <SkillsTable
        title={translation.pages.myTeam.metricLabels.skills()}
        skills={isDualMode ? (entityData.skillsInRange || []).slice(0, 10) : (entityData.skillsInRange || [])}
        isLoading={isLoadingComparison}
      />
    </AppBox>
  )

  return (
    <AppBox sx={isSidebarCollapsed ? styles.rootCollapsed : styles.root}>

      {/* Left Sidebar - Filters */}
      <AppPaper sx={isSidebarCollapsed ? styles.filterPaperCollapsed : styles.filterPaper}>
        <AppBox sx={styles.filterSection}>
          <Caption sx={styles.sectionLabel}>{translation.pages.myTeam.metricLabels.entity1()}</Caption>
          <Autocomplete
            size="small"
            options={myTeamStore.comparisonTeams}
            value={entity1}
            onChange={(_, newValue) => setEntity1(newValue)}
            getOptionLabel={option => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isLoadingOptions}
            renderInput={params => (
              <AppTextField
                {...params}
                placeholder={translation.pages.myTeam.metricLabels.selectTeam()}
                sx={styles.textField}
              />
            )}
            sx={styles.autocomplete}
          />
          <TeamLearningFilter
            fromDate={entity1FromDate}
            toDate={entity1ToDate}
            setFromDate={setEntity1FromDate}
            setToDate={setEntity1ToDate}
          />
        </AppBox>

        <AppBox sx={styles.filterSection}>
          <Caption sx={styles.sectionLabel}>{translation.pages.myTeam.metricLabels.entity2Optional()}</Caption>
          <Autocomplete
            size="small"
            options={myTeamStore.comparisonTeams}
            value={entity2}
            onChange={(_, newValue) => setEntity2(newValue)}
            getOptionLabel={option => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isLoadingOptions}
            renderInput={params => (
              <AppTextField
                {...params}
                placeholder={translation.pages.myTeam.metricLabels.selectTeamOptional()}
                sx={styles.textField}
              />
            )}
            sx={styles.autocomplete}
          />
          <TeamLearningFilter
            fromDate={entity2FromDate}
            toDate={entity2ToDate}
            setFromDate={setEntity2FromDate}
            setToDate={setEntity2ToDate}
          />
        </AppBox>

        <RatingFilter
          minRating={minRating}
          maxRating={maxRating}
          onRatingClick={handleRatingClick}
          isRatingSelected={isRatingSelected}
        />

        <AppBox sx={styles.buttonContainer}>
          <AppButton
            textValue={entity2 ? translation.pages.myTeam.metricLabels.compare() : translation.pages.myTeam.metricLabels.fetchInsights()}
            variant="contained"
            onClick={handleCompare}
            disabled={!entity1 || !entity1FromDate || !entity1ToDate || isLoadingComparison}
            sx={{ width: '100%', fontSize: 14, fontWeight: 500 }}
          />
          <AppButton
            textValue={translation.pages.myTeam.metricLabels.reset()}
            variant="light"
            onClick={handleReset}
            disabled={!entity1 && !entity2 && !comparisonData}
            sx={{ width: '100%', fontSize: 14, fontWeight: 500 }}
          />
        </AppBox>
      </AppPaper>

      {/* Right Content Area */}
      <AppPaper sx={styles.contentPaper}>
        {!comparisonData ? (
          <AppBox sx={styles.emptyStateContainer}>
            <Caption sx={styles.emptyText}>
              {translation.pages.myTeam.metricLabels.emptyStateMessage()}
            </Caption>
          </AppBox>
        ) : (
          <AppBox sx={styles.contentContainer}>
            <AppBox sx={isDualMode ? styles.dualEntityContainer : styles.singleEntityContainer}>
              {/* Entity 1 */}
              {renderEntityContent(comparisonData.entity1, generalMetrics, teamSpecificMetrics)}

              {/* Middle Column - Only in Dual Mode */}
              {isDualMode && (
                <AppBox sx={styles.middleColumn}>

                  {commonComparisonMetrics.length > 0 && (
                    <AppBox sx={styles.commonMetricsBox}>
                      <Caption sx={styles.metricsTitle}>{translation.pages.myTeam.comparisonMetrics()}</Caption>
                      <PageMetrics
                        sx={styles.comparisonMetricsGrid}
                        metrics={commonComparisonMetrics}
                        isLoading={isLoadingComparison}
                        size="small"
                      />
                    </AppBox>
                  )}

                  <AppBox sx={styles.chartContainer}>
                    <SkillGrowthChart
                      skills={comparisonData.skillGrowthChart?.skills || []}
                      isLoading={isLoadingComparison}
                      isDualMode={isDualMode}
                      entity1Name={comparisonData.entity1.name}
                      entity2Name={comparisonData.entity2?.name}
                    />
                  </AppBox>

                  {comparisonData.comparisonInsights && comparisonData.comparisonInsights.length > 0 && (
                    <AppBox sx={styles.insightsBox}>
                      <Caption sx={styles.metricsTitle}>{translation.pages.myTeam.comparisonInsights()}</Caption>
                      {comparisonData.comparisonInsights.map(insight => (
                        <AppBox key={insight} sx={styles.insightBullet}>
                          <Caption sx={styles.insightText}>• {insight}</Caption>
                        </AppBox>
                      ))}
                    </AppBox>
                  )}
                </AppBox>
              )}

              {/* Entity 2 - Only in Dual Mode */}
              {isDualMode && comparisonData.entity2 && renderEntityContent(
                comparisonData.entity2,
                entity2GeneralMetrics,
                entity2TeamSpecificMetrics,
              )}
            </AppBox>
          </AppBox>
        )}
      </AppPaper>
    </AppBox>
  )
})

const styles = {
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: { xs: 10, sm: 12, md: 15 },
    minHeight: { xs: 'auto', md: 'calc(100vh - 150px)' },
    height: { xs: 'auto', md: 'calc(100vh - 150px)' },
    position: 'relative',
  },
  rootCollapsed: {
    width: '100%',
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: 0,
    minHeight: { xs: 'auto', md: 'calc(100vh - 150px)' },
    height: { xs: 'auto', md: 'calc(100vh - 150px)' },
    position: 'relative',
  },
  filterPaper: {
    width: { xs: '100%', md: '35%', lg: '30%' },
    minWidth: { xs: 'auto', sm: '300px' },
    height: { xs: 'auto', md: '100%' },
    flexDirection: 'column',
    borderRadius: '10px',
    padding: { xs: '12px', sm: '15px' },
    gap: { xs: 12, md: 15 },
    overflowY: 'auto',
    display: 'flex',
    flexShrink: 0,
    opacity: 1,
    transition: 'width 0.3s ease-in-out, min-width 0.3s ease-in-out, opacity 0.3s ease-in-out, padding 0.3s ease-in-out',
  },
  filterPaperCollapsed: {
    width: '0 !important',
    minWidth: '0 !important',
    height: { xs: 'auto', md: '100%' },
    flexDirection: 'column',
    borderRadius: '10px',
    padding: '0 !important',
    gap: 0,
    overflowY: 'hidden',
    display: 'flex',
    flexShrink: 0,
    opacity: 0,
    transition: 'width 0.3s ease-in-out, min-width 0.3s ease-in-out, opacity 0.3s ease-in-out, padding 0.3s ease-in-out',
  },
  filterSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: 8, sm: 10 },
    width: '100%',
  },
  sectionLabel: {
    fontSize: 11,
    color: 'text.secondary',
  },
  textField: {
    fontSize: 12,
    m: 0,
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'background.dark',
      borderRadius: '10px',
    },
  },
  autocomplete: {
    width: '100%',
    backgroundColor: 'background.dark',
    borderRadius: '10px',
    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
      color: 'text.primary',
    },
  },
  buttonContainer: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
    gap: { xs: 8, sm: 10 },
    mt: 1,
    width: '100%',
  },
  contentPaper: {
    flex: 1,
    height: { xs: 'auto', md: '100%' },
    minHeight: { xs: '400px', md: '600px' },
    flexDirection: 'column',
    borderRadius: '10px',
    padding: { xs: '12px', sm: '15px' },
    gap: { xs: 12, md: 15 },
    overflowY: 'auto',
    display: 'flex',
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: 12, md: 15 },
    height: '100%',
  },
  singleEntityContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: 12, md: 15 },
  },
  dualEntityContainer: {
    display: 'flex',
    flexDirection: { xs: 'column', lg: 'row' },
    gap: 15,
    height: '100%',
  },
  entityColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: 10, md: 12 },
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  middleColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: 12, md: 15 },
    alignItems: 'center',
    width: { xs: '100%', lg: '370px' },
    flexShrink: 0,
  },
  commonMetricsBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    width: '100%',
    padding: '10px 15px',
    borderRadius: '10px',
    backgroundColor: 'background.dark',
  },
  chartContainer: {
    width: '100%',
    flex: 1,
    minHeight: '300px',
    maxHeight: '500px',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '10px 15px',
    borderRadius: '10px',
    backgroundColor: 'background.dark',
  },
  insightsBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    width: '100%',
    padding: '10px 15px',
    borderRadius: '10px',
    backgroundColor: 'background.dark',
  },
  insightBullet: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  insightText: {
    fontSize: { xs: 10, md: 11 },
    color: 'text.primary',
    lineHeight: 1.4,
  },
  entityName: {
    fontSize: { xs: 14, md: 16 },
    fontWeight: 600,
    color: 'text.primary',
  },
  period: {
    fontSize: { xs: 10, md: 11 },
    color: 'text.secondary',
  },
  metricsTitle: {
    fontSize: { xs: 11, md: 12 },
    color: 'text.secondary',
    fontWeight: 500,
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
    gap: { xs: 10, sm: 12, md: 15 },
    width: '100%',
  },
  comparisonMetricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 15,
    width: '100%',
  },
  emptyStateContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    flex: 1,
    padding: { xs: 2, sm: 3 },
  },
  emptyText: {
    fontSize: 12,
    color: 'text.secondary',
    textAlign: 'center',
  },
  expandButton: {
    color: 'text.secondary',
    padding: '4px',
    width: 'fit-content',
    cursor: 'pointer',
  },
  expandText: {
    fontSize: 11,
    color: 'text.secondary',
    fontWeight: 500,
  },
}